import {SIDEBAR_ROUTES} from '~/src/routes'

/**
 * Navigation Menu Items
 *
 * NOTE: URLs no longer contain role prefixes (e.g., /platform-admin/, /agency-admin/)
 * The layout variant is determined from the user's role in AppRoutes, not from the URL
 *
 * moduleKey: Maps to the permission module for canView checks
 * If undefined, the menu item is always visible
 */
export interface INavMenuItem {
  id: number
  link?: string
  text: string
  subMenuItems?: INavMenuItem[]
  activePattern?: string // Optional regex pattern to detect if this menu item should be active
  moduleKey?: string // Maps to permission module key for visibility checks
}

export const platformAdminList: INavMenuItem[] = [
  {
    id: 1,
    link: SIDEBAR_ROUTES.DASHBOARD,
    text: 'Dashboard',
    moduleKey: 'dashboard',
  },
  {
    id: 2,
    link: SIDEBAR_ROUTES.MANAGE_COMPANIES,
    text: 'Manage Companies',
    moduleKey: 'manage_companies',
  },
  {
    id: 3,
    link: SIDEBAR_ROUTES.MANAGE_AGENCIES,
    text: 'Manage Agencies',
    moduleKey: 'manage_agencies',
  },
  {
    id: 4,
    link: '/users-roles',
    text: 'Users',
    moduleKey: 'users_roles',
  },
  {
    id: 5,
    link: SIDEBAR_ROUTES.PAYROLL,
    text: 'Payroll',
    moduleKey: 'payroll',
  },
  {
    id: 6,
    link: SIDEBAR_ROUTES.CANDIDATES,
    text: 'Candidates',
    moduleKey: 'candidates',
  },
  // Note: Global dropdown is handled by GlobalDropdown.tsx component, not MenuItems
  // {
  //   id: 7,
  //   link: SIDEBAR_ROUTES.IMPORT,
  //   text: 'Import Candidates',
  // },
]

export const agencyAdminList: INavMenuItem[] = [
  {
    id: 1,
    link: SIDEBAR_ROUTES.DASHBOARD,
    text: 'Dashboard',
    moduleKey: 'dashboard',
  },
  {
    id: 2,
    link: SIDEBAR_ROUTES.MANAGE_COMPANIES,
    text: 'Companies',
    moduleKey: 'manage_companies',
  },
  {
    id: 3,
    text: 'Manage Orders',
    moduleKey: 'manage_orders',
    subMenuItems: [
      {
        id: 10,
        link: SIDEBAR_ROUTES.PENDING_SERVICE_ORDERS,
        text: 'Pending Orders',
        moduleKey: 'manage_orders',
      },
      {
        id: 11,
        link: SIDEBAR_ROUTES.SERVICE_ORDERS,
        text: 'Current Orders',
        moduleKey: 'manage_orders',
      },
      {
        id: 13,
        link: SIDEBAR_ROUTES.COMPLETED_SERVICE_ORDERS,
        text: 'Completed Orders',
        moduleKey: 'manage_orders',
      },
    ],
  },
  {
    id: 4,
    link: SIDEBAR_ROUTES.MANAGE_AGENCY,
    text: 'Manage Profile',
    activePattern: '/manage-agency', // Matches /manage-agency/*
    moduleKey: 'manage_profile',
  },
  {
    id: 5,
    link: SIDEBAR_ROUTES.CANDIDATES,
    text: 'Candidates',
    moduleKey: 'candidates',
  },
  {
    id: 6,
    link: '/users-roles',
    text: 'Users & Roles',
    // Uses agency_users_roles for agency admin (submodule of manage_profile)
    // Route permission check has aliases to handle this
    moduleKey: 'users_roles',
  },
]

/**
 * Company Admin Navigation
 *
 * API modulePermissions for companyadmin:
 * - dashboard: Dashboard
 * - create_orders: Create Orders (button, not nav item)
 * - company_manage_profile: Manage Profile (contains company_positions, company_users_roles, etc.)
 * - company_manage_orders: Manage Orders (service_orders, job_orders)
 * - other_settings: Other Settings
 * - company_invoices: Invoices
 * - review_approve: Review and Approve
 * - company_timesheets: Timesheets
 * - company_documents: Documents
 *
 * Note: "Positions" nav uses company_positions (submodule of company_manage_profile)
 * The same data is shown in both nav Positions and Manage Profile -> Positions tab
 */
export const companyAdminList: INavMenuItem[] = [
  {
    id: 1,
    link: SIDEBAR_ROUTES.DASHBOARD,
    text: 'Dashboard',
    moduleKey: 'dashboard',
  },
  {
    id: 2,
    link: SIDEBAR_ROUTES.POSITIONS,
    text: 'Positions',
    // Uses company_positions which is a submodule of company_manage_profile
    moduleKey: 'company_positions',
  },
  {
    id: 3,
    text: 'Manage Orders',
    moduleKey: 'company_manage_orders',
    subMenuItems: [
      {
        id: 11,
        link: SIDEBAR_ROUTES.SERVICE_ORDERS,
        text: 'Service Orders',
        moduleKey: 'service_orders',
        // Only match exact /manage-orders path, not completedOrders or candidate-list
        activePattern: '/manage-orders(?!/(completedOrders|candidate-list))',
      },
      {
        id: 12,
        link: SIDEBAR_ROUTES.COMPLETED_SERVICE_ORDERS,
        text: 'Completed Orders',
        moduleKey: 'service_orders',
        // Match both completed orders page and candidate list view
        activePattern: '/manage-orders/(completedOrders|candidate-list)',
      },
    ],
  },
  {
    id: 4,
    link: SIDEBAR_ROUTES.MANAGE_COMPANY,
    text: 'Manage Profile',
    activePattern: '/manage-companies/[^/]+', // Matches /manage-companies/{companyId}/*
    moduleKey: 'company_manage_profile',
  },
  {
    id: 5,
    link: '/invoices',
    text: 'Invoices',
    moduleKey: 'company_invoices',
  },
  {
    id: 6,
    link: '/timesheets',
    text: 'Timesheets',
    moduleKey: 'company_timesheets',
  },
  {
    id: 7,
    link: '/users-roles',
    text: 'Users & Roles',
    // Uses company_users_roles which is a submodule of company_manage_profile
    moduleKey: 'company_users_roles',
  },
]

export const mspAgencyList: INavMenuItem[] = [
  {
    id: 1,
    link: SIDEBAR_ROUTES.DASHBOARD,
    text: 'Dashboard',
    moduleKey: 'dashboard',
  },
  {
    id: 2,
    link: SIDEBAR_ROUTES.MANAGE_COMPANIES,
    text: 'Companies',
    moduleKey: 'manage_companies',
  },
  {
    id: 3,
    text: 'Manage Orders',
    moduleKey: 'manage_orders',
    subMenuItems: [
      {
        id: 10,
        link: SIDEBAR_ROUTES.PENDING_SERVICE_ORDERS,
        text: 'Pending Orders',
        moduleKey: 'manage_orders',
      },
      {
        id: 11,
        link: SIDEBAR_ROUTES.SERVICE_ORDERS,
        text: 'Current Orders',
        moduleKey: 'manage_orders',
      },
      {
        id: 13,
        link: SIDEBAR_ROUTES.COMPLETED_SERVICE_ORDERS,
        text: 'Completed Orders',
        moduleKey: 'manage_orders',
      },
    ],
  },
  {
    id: 4,
    link: SIDEBAR_ROUTES.MANAGE_PROFILE,
    text: 'Manage Profile',
    activePattern: '/manage-profile', // Matches /manage-profile/*
    moduleKey: 'manage_profile',
  },
  {
    id: 5,
    link: SIDEBAR_ROUTES.CANDIDATES,
    text: 'Candidates',
    moduleKey: 'candidates',
  },
  {
    id: 6,
    link: '/users-roles',
    text: 'Users & Roles',
    moduleKey: 'users_roles',
  },
]

/**
 * General Admin Navigation
 *
 * API modulePermissions for general_admin:
 * - global: Global (with roles_permissions submodule) - accessed via GlobalDropdown
 * - manage_companies: Manage Companies (12 submodules)
 * - manage_agencies: Manage Agencies (13 submodules)
 * - directory: Directory (view only)
 *
 * Note: GA is a cross-agency admin role for managing companies/agencies without operational access
 * The "Global" dropdown is added separately in NavMenuBar, not as a nav item here
 */
export const generalAdminList: INavMenuItem[] = [
  {
    id: 1,
    link: SIDEBAR_ROUTES.MANAGE_COMPANIES,
    text: 'Manage Companies',
    moduleKey: 'manage_companies',
  },
  {
    id: 2,
    link: SIDEBAR_ROUTES.MANAGE_AGENCIES,
    text: 'Manage Agencies',
    moduleKey: 'manage_agencies',
  },
  {
    id: 3,
    link: '/directory',
    text: 'Directory',
    moduleKey: 'directory',
  },
]

export const mainAgencyAdminList: INavMenuItem[] = [
  {
    id: 1,
    link: SIDEBAR_ROUTES.DASHBOARD,
    text: 'Dashboard',
    moduleKey: 'dashboard',
  },
  {
    id: 2,
    link: SIDEBAR_ROUTES.MANAGE_COMPANIES,
    text: 'Companies',
    moduleKey: 'manage_companies',
  },
  {
    id: 3,
    text: 'Manage Orders',
    moduleKey: 'manage_orders',
    subMenuItems: [
      {
        id: 10,
        link: SIDEBAR_ROUTES.PENDING_SERVICE_ORDERS,
        text: 'Pending Orders',
        moduleKey: 'manage_orders',
      },
      {
        id: 11,
        link: SIDEBAR_ROUTES.SERVICE_ORDERS,
        text: 'Current Orders',
        moduleKey: 'manage_orders',
      },
      {
        id: 13,
        link: SIDEBAR_ROUTES.COMPLETED_SERVICE_ORDERS,
        text: 'Completed Orders',
        moduleKey: 'manage_orders',
      },
    ],
  },
  {
    id: 4,
    link: SIDEBAR_ROUTES.MANAGE_AGENCY,
    text: 'Manage Profile',
    activePattern: '/manage-agency', // Matches /manage-agency/*
    moduleKey: 'manage_profile',
  },
  {
    id: 5,
    link: SIDEBAR_ROUTES.CANDIDATES,
    text: 'Candidates',
    moduleKey: 'candidates',
  },
  {
    id: 6,
    link: '/users-roles',
    text: 'Users & Roles',
    moduleKey: 'users_roles',
  },
]
